<template>
	<div class="card">
		<div style="margin-bottom:10px;" class="grid ml-0 mr-1">
			<div class="flex flex-auto p-input-icon-left p-input-icon-right mt-1">
				<i class="pi pi-users" />
				<InputText v-model="filterText" @input='filterChanged' placeholder="Keyword filter.." />
				<i class="pi pi-search" />
			</div>
			<div class="flex align-items-center">
				<a @click="companyListExcelExport" class="css-image excel-icon mt-1" v-tooltip.top="'Export to Excel'"></a>

				<div class="flex ml-4 mr-4">
					<a class="css-image" v-bind:class="filterNorthIsland ? 'north-island-icon':'north-island-disabled-icon'" v-tooltip.top="'Show companies operating in North Island'" style="position: absolute; margin-top: -15px; height: 20px; width: 30px;" @click="filterNorthIsland = !filterNorthIsland;filterSouthIsland = false;" />
					<a class="css-image" v-bind:class="filterSouthIsland ? 'south-island-icon':'south-island-disabled-icon'" v-tooltip.top="'Show companies operating in South Island'" style="position: absolute; margin-top: 5px; height: 20px; width: 30px; " @click="filterSouthIsland = !filterSouthIsland;filterNorthIsland = false;" />
				</div>

				<InputSwitch v-model="onlyActive" class="ml-4 mr-1 mt-1" />
				<a class="mr-4 mt-1" @click="onlyActive=!onlyActive">Only active</a>
				<Button icon="pi pi-plus" label="New" @click="newCompany" class="p-button-rounded mt-1" />
			</div>
		</div>
		<Message v-if="companyList.errorMessage" severity="error" style="white-space:pre-wrap">{{companyList.errorMessage}}</Message>
		<div style="width:100%;height: calc(100vh - 260px);" v-if="companyList.hasReceivedData">
			<DataTable :value="filteredCompanyList"
					   datakey="id"
					   :paginator="true"
					   :rows="100"
					   :scrollable="true"
					   scrollHeight="flex"
					   :rowClass="applyRowClass"
					   stripedRows
					   class="p-datatable-sm"
					   :paginatorTemplate="paginatorTemplate"
					   :rowsPerPageOptions="[10,20,50,100,200]"
					   responsiveLayout="scroll"
					   currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">
				<Column field="id" header="ID" style="flex-grow:0;flex-basis:70px">
					<template #body="{data}">
						<router-link :to="{ name: 'CompanyDetails', params: { companyId: data.id }}">{{data.id}}</router-link>
					</template>
				</Column>
				<Column field="name" header="Name" style="flex-basis:300px;">
					<template #body="{data}">
						<router-link :to="{ name: 'CompanyDetails', params: { companyId: data.id }}">{{data.name}}</router-link>
						<Badge v-if="!isActive(data)" class="small-grey-badge ml-1" value="inactive" />
					</template>
				</Column>
				<Column field="loc" header="Location" class="hidden md:flex" style="flex-basis:100px;">
					<template #body="{data}">
						<router-link :to="{ name: 'CompanyDetails', params: { companyId: data.id }}">{{data.loc}}</router-link>

						<Badge class="css-image new-zealand-icon island-badge ml-1" v-tooltip.top="'Operates in North & South Island'" v-if="data.isle == 'NI SI'" />

						<Badge class="css-image north-island-icon island-badge ml-1" v-tooltip.top="'Operates in North Island'" v-else-if="data.isle == 'NI'" />

						<Badge class="css-image south-island-icon island-badge ml-1" v-tooltip.top="'Operates in South Island'" v-else-if="data.isle == 'SI'" />

					</template>
				</Column>
				<Column field="conts" header="Contacts" class="hidden md:flex" style="flex-basis:300px;">
					<template #body="{data}">
						<router-link :to="{ name: 'CompanyDetails', params: { companyId: data.id }}">{{data.conts}}</router-link>
					</template>
				</Column>
				<Column class="hidden md:flex" header="Properties" style="flex-basis:50px;">
					<template #body="{data}">
						<router-link :to="{ name: 'CompanyDetails', params: { companyId: data.id }}">{{data.np}}</router-link>
					</template>
				</Column>
			</DataTable>
		</div>
		<ProgressSpinner v-if="isLoading" />
	</div>
</template>

<script lang="ts">

	import { Vue, Options } from 'vue-class-component';
	import { CompanyListData, GetFilteredCompanySummaryListQuery } from '@/api-client/api-client';
	import { CompanyListFlags } from '@/utils/CompanyListFlags';
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import filterListBySearchString from '../utils/StringSearch';
	import { DownloadFromFileResponse } from '../utils/DownloadFromFileResponse';
	import Toast from '@/utils/Toast';
	import { DataTableRowClickEvent } from 'primevue/datatable';
	import { CompanyType } from '@/utils/CompassConstants';

	@Options({
		name: 'CompanyList',
		components: {
		},
		props: {
			includeSuppliers: Boolean,
			includeOwners: Boolean,
			includeCustomers: Boolean,
			includeAuthorities: Boolean,
		}
	})
	export default class CompanyList extends Vue {

		// Data
		filterText = ""
		debounceTimerRef = -1;
		debouncedFilterText = ""
		includeSuppliers = false;
		includeOwners = false;
		includeCustomers = false;
		includeAuthorities = false;
		onlyActive = true;
		filterNorthIsland = false;
		filterSouthIsland = false;

		// Computed isDesktop
		get isDesktop() {
			return window.innerWidth >= 992;
		}

		// Computed to simplify paginator on mobile
		get paginatorTemplate() {
			return this.isDesktop ?
				"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
				: "PrevPageLink NextPageLink"
		}

		filterChanged(_ev: InputEvent) {
			const timeoutMillseconds = 100;
			clearTimeout(this.debounceTimerRef);
			this.debounceTimerRef = setTimeout(() => this.debouncedFilterText = this.filterText, timeoutMillseconds);
		}

		// Helpers
		applyRowClass(data: CompanyListData) {
			return CompanyListFlags.isActive(data) ? "" : "row-inactive";
		}

		// Action
		click(ev: DataTableRowClickEvent) {
			this.$router.push({
				name: 'CompanyDetails',
				params: { companyId: ev.data.id }
			});
		}

		newCompany() {
			let companyType = CompanyType.Company;

			if (this.includeCustomers) {
				companyType = CompanyType.Customer;
			} else if (this.includeSuppliers) {
				companyType = CompanyType.Supplier;
			} else if (this.includeOwners) {
				companyType = CompanyType.Owner;
			}

			this.$router.push({
				name: 'NewCompany',
				query: {
					companyType: companyType
				}
			});
		}

		isActive(data: CompanyListData): boolean {
			return CompanyListFlags.isActive(data);
		}

		companyListExcelExport() {

			// Prepare payload to send to server
			const companyListData = new GetFilteredCompanySummaryListQuery();
			companyListData.queryString = this.filterText;
			companyListData.onlyActive = this.onlyActive;
			companyListData.includeAuthorities = this.includeAuthorities;
			companyListData.includeCustomers = this.includeCustomers;
			companyListData.includeOwners = this.includeOwners;
			companyListData.includeSuppliers = this.includeSuppliers;
			companyListData.filterNorthIsland = this.filterNorthIsland;
			companyListData.filterSouthIsland = this.filterSouthIsland;

			const apiClient = CompassApi.companyApi(this.$store);
			apiClient.companyListExcelExport(companyListData)
				.then(result => {

					// Download Popup
					if (result)
						DownloadFromFileResponse(result);

					Toast.RemoveAllGroups();
					Toast.Success('Company List Excel export', 'The export was successful.');
				})
				.catch(error => {
					Toast.ApiError("Error exporting to excel", error);
				});
		}

		// Computed
		get companyList() {
			return this.$store.state.companyList;
		}

		get isLoading(): boolean {
			if (this.companyList == null)
				return true;
			return this.companyList.isLoading && !(this.companyList.hasReceivedData);
		}

		get filteredCompanyList() {
			let list = this.companyList.data;

			if (this.onlyActive) {
				list = list.filter(c => CompanyListFlags.isActive(c));
			}
			list = list.filter(c =>
				(this.includeSuppliers && CompanyListFlags.isSupplier(c)) ||
				(this.includeOwners && CompanyListFlags.isOwner(c)) ||
				(this.includeCustomers && CompanyListFlags.isCustomer(c)) ||
				(this.includeAuthorities && CompanyListFlags.isRegionalAuthority(c)));
			if (this.filterNorthIsland) {
				list = list.filter(c => c.isle?.includes("NI"));
			}
			if (this.filterSouthIsland) {
				list = list.filter(c => c.isle?.includes("SI"));
			}

			list = filterListBySearchString(list, this.filterText, (company: CompanyListData) => company.id + ' ' + company.name + ' ' + company.conts + ' ' + company.loc + ' ' + company.isle);

			return list;
		}
	}
</script>

<style lang="scss">
</style>